
import { computed, defineComponent, inject, reactive, ref, toRefs, watch } from 'vue';
import { CheckmarkCircleOutline as ValidIcon, CloseCircleOutline as InvalidIcon } from '@vicons/ionicons5';
import { useVModel } from 'vue-composable';
import { DateTime } from 'luxon';
import responseService from '@/services/response-service';
import { ShowToast } from '@/providers/Toast/toast-types';
import { SendInviteStatus } from '@/types';

type ModalState = {
  emails: string;
};

const MAX_FAILED_IN_TOAST = 3;

export default defineComponent({
  name: 'SendModal',
  components: { ValidIcon, InvalidIcon },
  props: {
    surveyId: {
      type: String,
      required: true
    },
    surveyName: {
      type: String,
      required: true
    },
    showModal: {
      type: Boolean
    }
  },
  emits: ['sent'],
  setup: (props, { emit }) => {
    const showToast = inject<ShowToast>('showToast')!;
    const showModalRef = useVModel(props, 'showModal');
    const surveyNameRef = useVModel(props, 'surveyName');
    const surveyId = useVModel(props, 'surveyId');
    const validEmailRef = ref(false);
    const sendingRef = ref(false);

    const modalState: ModalState = reactive({
      emails: ''
    });

    // Lowercased to match the server, which treats case variants as one recipient.
    const enteredEmails = computed(() =>
      modalState.emails
        .split(/[\n,; ]/g)
        .map((email) => email?.trim().toLowerCase())
        .filter(Boolean)
    );

    const emailList = computed(() => enteredEmails.value.filter((value, index, self) => self.indexOf(value) === index));

    const duplicatesRemoved = computed(() => enteredEmails.value.length - emailList.value.length);

    const invalidEmails = () => {
      const invalidEmailList = modalState.emails
        .split(/[\n,; ]+/g)
        .map((email) => email?.trim())
        .filter(Boolean)
        .some((email) => !RegExp(/[^@ \t\r\n]+@[^@ \t\r\n]+\.[^@ \t\r\n]+/).test(email));

      validEmailRef.value = !invalidEmailList;
    };

    const onPositiveClick = async () => {
      if (sendingRef.value) return;
      sendingRef.value = true;

      try {
        const outcomes = await Promise.all(
          emailList.value.map((email) =>
            responseService
              .create(surveyId.value, {
                surveyId: surveyId.value,
                email,
                createdOn: DateTime.now().toISO()
              })
              .then(
                ({ status }) => ({ email, status }),
                () => ({ email, status: 'failed' as const })
              )
          )
        );

        const countOf = (status: SendInviteStatus) => outcomes.filter((outcome) => outcome.status === status).length;
        const failed = outcomes.filter((outcome) => outcome.status === 'failed').map((outcome) => outcome.email);
        const summaryParts = [`Sent: ${countOf('sent')}`];
        if (countOf('alreadyInvited') > 0) summaryParts.push(`Already invited: ${countOf('alreadyInvited')}`);
        if (duplicatesRemoved.value > 0) summaryParts.push(`Duplicates removed: ${duplicatesRemoved.value}`);
        const summary = summaryParts.join(' · ');

        emit('sent', countOf('sent'));

        if (failed.length === 0) {
          showToast({ headline: 'Survey sent', message: summary, color: 'success' });
          modalState.emails = '';
          showModalRef.value = false;
          return;
        }

        let listed = failed.slice(0, MAX_FAILED_IN_TOAST).join(', ');
        if (failed.length > MAX_FAILED_IN_TOAST) listed += ` and ${failed.length - MAX_FAILED_IN_TOAST} more`;
        showToast({
          headline: `Failed: ${failed.length}`,
          message: `${summary}. Couldn't send to ${listed}. They're left in the list; send again to retry.`,
          color: 'warning'
        });

        // Only the failures stay, so Send retries them without re-counting the rest as already invited.
        modalState.emails = failed.join(', ');
      } finally {
        sendingRef.value = false;
      }
    };

    watch(
      () => modalState.emails,
      () => invalidEmails()
    );

    return {
      ...toRefs(modalState),
      emailList,
      duplicatesRemoved,
      showModalRef,
      validEmailRef,
      sendingRef,
      surveyNameRef,
      onPositiveClick
    };
  }
});
